import { List } from '@factorialco/deprecated-design-system'
import {
  EmployeesEmployeeSelectorInput,
  ProjectManagementProjectRoleRoleEnum,
  ProjectRolesDocument,
} from 'generated/resources/graphql'

import Loading from 'modules/core/components/Loading'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import AddRole from './AddRole'
import EmptyRoleList from './EmptyRoleList'
import RoleRow from './RoleRow'

export interface RolesSelectorProps {
  projectId: number
  onCreateRow: (viewerEmployeeSelection: EmployeesEmployeeSelectorInput) => void
  onUpdateRow: (projectRoleId: number, role: ProjectManagementProjectRoleRoleEnum) => void
  onDeleteRow: (projectRoleId: number) => void
  useModal?: boolean
  canAssignOwners: boolean
  canAssignEditors: boolean
  currentEmployeeId: number
}

export const RolesSelector = ({
  projectId,
  onCreateRow,
  onUpdateRow,
  onDeleteRow,
  canAssignOwners,
  canAssignEditors,
  currentEmployeeId,
}: RolesSelectorProps) => {
  const { data, loading } = useQuery(ProjectRolesDocument, {
    variables: { projectIds: [projectId] },
    skip: !projectId,
  })

  const projectRoles = data ? data.projectManagement.projectRoles : []

  const isThatYou = (employeeId: number) => {
    return employeeId === currentEmployeeId
  }

  const disabledEmployeeIds = projectRoles.flatMap((role) =>
    role.employee ? [role.employee.id] : []
  )

  const onTrackedAddRow = (viewerEmployeeSelection: EmployeesEmployeeSelectorInput) => {
    track(trackingEvents.projectManagement.settings.addRole, {
      selection_type: viewerEmployeeSelection.type,
    })

    onCreateRow(viewerEmployeeSelection)
  }

  const onTrackedUpdateRow = (
    projectRoleId: number,
    role: ProjectManagementProjectRoleRoleEnum
  ) => {
    track(trackingEvents.projectManagement.settings.updateRole, {
      new_role: role,
    })

    onUpdateRow(projectRoleId, role)
  }

  const onTrackedDeleteRow = (projectRoleId: number) => {
    track(trackingEvents.projectManagement.settings.removeRole, {
      role: projectRoles.find((role) => role.id === projectRoleId)?.role,
    })

    onDeleteRow(projectRoleId)
  }

  if (loading) {
    return <Loading />
  }

  return (
    <>
      {projectRoles.length === 0 && <EmptyRoleList />}
      {projectRoles.length !== 0 && (
        <List>
          {projectRoles.map((role, index) => {
            if (role.employee) {
              const isYou = isThatYou(role.employee.id)
              const employeeName = `${role.employee.preferredName || role.employee.firstName} ${
                role.employee.lastName
              }`

              return (
                <RoleRow
                  key={index}
                  editable={!isYou}
                  role={role.role}
                  roleId={role.id}
                  employeeName={employeeName}
                  employee={role.employee}
                  onUpdateRow={onTrackedUpdateRow}
                  onDeleteRow={onTrackedDeleteRow}
                  canAssignOwners={canAssignOwners}
                  canAssignEditors={canAssignEditors}
                />
              )
            }
          })}
        </List>
      )}
      <AddRole
        disabledEmployeeIds={disabledEmployeeIds}
        onCreateRow={onTrackedAddRow}
        buttonCentered={projectRoles.length === 0}
      />
    </>
  )
}
